import { ButcherHeader } from '@components/ButcherHeader';
import { Assets } from '@helpers/Constants/Assets';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEllipsisVertical,
  faSearch,
} from '@fortawesome/free-solid-svg-icons';
import { SideNav } from '@components/OrderCreation/OrderSideNav';
import Pagination from '@components/Pagination';
import { useEffect, useState, useRef } from 'react';
import {
  createCustomerBreadCrumbs,
  errorMessage,
  ExistingCustomerCrumbSteps,
  pagination,
} from '@helpers/Constants';
import { ApiEndpoints, FrontendRoutes } from '@helpers/Constants/Navigation';
import { Axios } from '@helpers/ApiService';
import { CustomerProps } from '@helpers/Models';
import { useToast } from '@contexts/Toast';
import { generatePath, useLocation, useNavigate } from 'react-router-dom';
import { OrderListBreadcrumbs } from '@components/OrderProcessing/OrderListBreadcrumbs';
import { OrderBreadcrumb } from '@components/OrderCreation/OrderBreadcrumbs';
import {
  getFormattedPhoneNumber,
  handleNewOrderInitiation,
} from '@services/OrderCreationService';
import { useDismissDropdown } from '@services/DropdownService';
import { isAxiosError } from 'axios';
import { SubscriptionValidation } from '@services/SubscriptionValidation';

export const CustomerList: React.FC = () => {
  const navigate = useNavigate();
  const { showToastMessage } = useToast();
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(pagination.itemsPerPage);
  const [totalPages, setTotalPages] = useState(1);
  const [searchKey, setSearchKey] = useState('');
  const [customers, setCustomers] = useState<CustomerProps[]>([]);
  const [activeDropdown, setActiveDropdown] = useState<number | null>(null);
  const dropdownRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const location = useLocation();
  const fromModal = location.state?.fromModal;
  const fromList = location.state?.fromList;
  const [isSubscriptionActive, setIsSubscriptionActive] =
    useState<boolean>(false);

  useEffect(() => {
    // Verify the subscription status
    setIsSubscriptionActive(SubscriptionValidation());

    if (searchKey != '') {
      setCurrentPage(1);
    }
    fetchCustomers(currentPage, searchKey, itemsPerPage);
  }, [currentPage, itemsPerPage, searchKey]);

  /**
   * Fetches a list of customers from the server and updates the state with the retrieved data.
   *
   * @param page - The current page number to fetch.
   * @param searchKey - The search keyword to filter customers.
   * @param count - The number of customers to fetch per page. Defaults to `itemsPerPage`.
   * @returns A promise that resolves to void.
   *
   * @throws Will show a toast message with an error if the fetch operation fails.
   */
  const fetchCustomers = async (
    page: number,
    searchKey: string,
    count: number = itemsPerPage,
  ): Promise<void> => {
    try {
      const response = await Axios.post(ApiEndpoints.FETCH_CUSTOMERS, {
        page,
        count,
        searchKey,
      });

      const data = response?.data?.data;
      setCustomers(data.customers);
      setCurrentPage(data.currentPage || 1);
      setTotalPages(data.totalPages || 1);
    } catch (error) {
      showToastMessage(errorMessage.FETCH_ERROR, 'error');
      //if error is an Axios error, show the error message
      if (isAxiosError(error)) {
        showToastMessage(
          error.response?.data?.message || errorMessage.DEFAULT_ERROR_MESSAGE,
          'error',
        );
      } else {
        showToastMessage(errorMessage.DEFAULT_ERROR_MESSAGE, 'error');
      }
    }
  };

  const toggleDropdown = (customerId: number) => {
    setActiveDropdown(activeDropdown === customerId ? null : customerId);
  };

  /**
   * useDismissDropdown
   *
   * A custom React hook that listens for clicks outside the provided element refs,
   * and triggers a callback when such an event occurs. Typically used to close dropdowns or popovers.
   *
   * @param {Object} props - The hook parameters.
   * @param {React.RefObject<HTMLElement>[]} props.refs - An array of refs to dropdown elements.
   *        The hook checks if a click occurs outside all these elements.
   * @param {() => void} props.onClose - A callback function that is called when a click occurs outside the referenced elements.
   *
   */
  useDismissDropdown({
    refs: Array.from(dropdownRefs.current.values()).map((el) => ({
      current: el,
    })),
    onClose: () => setActiveDropdown(null),
  });

  /**
   * Prepares and initiates the order creation process for a given customer.
   *
   * This function:
   * - Extracts the necessary customer details from the full `customer` object.
   * - Passes those details to the `handleNewOrderInitiation` for further processing.
   *
   * @remarks
   * This function assumes that a valid `customer` object is accessible in scope.
   * If the `customer` is not defined, the function exits without performing any actions.
   */
  const handleCreateNewOrder = (customerId: number): void => {
    const customer = customers.find((customer) => customer.id === customerId);
    if (customer) {
      const customerDetails = {
        id: customer.id,
        firstName: customer.firstName,
        lastName: customer.lastName,
        phoneHome: customer.phoneHome,
        primaryEmail: customer.primaryEmail,
        HuntingLicences: customer.HuntingLicences,
      };
      handleNewOrderInitiation(navigate, customerDetails);
    }
  };

  const FromAllOrderBreadCrumb = {
    steps: ExistingCustomerCrumbSteps,
    activeItem: 'Existing Customers',
  };

  return (
    <div className="flex h-screen w-screen gap-1">
      {/* Sidebar */}
      <SideNav active={2} />
      {/* Main Content */}
      <div className="flex-1 flex flex-col p-1 overflow-auto relative">
        {/* Header */}
        <ButcherHeader />
        {/* If its from All order list, show breadcurmbs with All order */}
        {fromModal ? (
          fromList === 'all_order' && (
            <OrderListBreadcrumbs orderInfo={FromAllOrderBreadCrumb} />
          )
        ) : (
          // Else, the customer breadcrumb

          <OrderBreadcrumb
            steps={createCustomerBreadCrumbs}
            activeItem="Customers"
          />
        )}
        <div className="flex justify-between items-center mb-6 mt-6">
          <div className="flex items-center gap-2 mt-3">
            <h1 className="font-semibold text-xl pl-4">Existing Customers</h1>
          </div>

          <div className="flex items-center gap-2">
            <div className="mt-auto pt-4">
              <button
                type="button"
                onClick={() => navigate(FrontendRoutes.CREATE_CUSTOMER)}
                className="w-full px-6 py-2 mb-4 border border-transparent rounded-sm text-sm font-medium text-white bg-primary hover:bg-dark-primary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary flex items-center justify-center space-x-2"
              >
                + Add New Customer
              </button>
            </div>
            <div className="relative">
              <input
                type="text"
                placeholder="Search..."
                onChange={(e) => setSearchKey(e.target.value)}
                value={searchKey}
                className="border p-2 pl-3 pr-10 w-80 accent-primary focus:outline-primary border-gray-300 rounded"
              />
              <FontAwesomeIcon
                icon={faSearch}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500"
              />
            </div>

          </div>

        </div>
        <div className="shadow-lg mt-3 rounded-lg">
          <form>
            <table className="w-full table-auto rounded-xl">
              <thead className="bg-gray-100">
                <tr className="border-b">
                  <th className="px-4 py-2">
                    <div className="flex items-center gap-1 font-semibold">
                      <span>Name</span>
                      <img
                        src={Assets.SORT_ICON}
                        alt="Sort"
                        className="h-3 w-3"
                      />
                    </div>
                  </th>
                  <th className="px-4 py-2 text-center font-semibold">Phone</th>
                  <th className="px-4 py-2 text-center font-semibold">Email</th>
                  <th className="px-4 py-2 text-center font-semibold">
                    Action
                  </th>
                  <th className="px-4 py-2 text-center"></th>
                </tr>
              </thead>
              <tbody>
                {customers.length > 0 ? (
                  customers.map((customer) => (
                    <tr
                      onClick={() => {
                        navigate(
                          generatePath(FrontendRoutes.CUSTOMER_DETAILS, {
                            customerId: customer.id,
                          }),
                        );
                      }}
                      key={customer.id}
                      className="hover: cursor-pointer"
                    >
                      <td className="px-4 py-2 font-semibold">
                        {customer.firstName} {customer.lastName}
                      </td>
                      <td className="px-4 py-2 text-center whitespace-nowrap">
                        {getFormattedPhoneNumber(customer.phoneHome)}
                      </td>
                      <td className="px-4 py-2 text-center">
                        {customer.primaryEmail}
                      </td>
                      <td className="px-4 py-2 text-center relative">
                        <button
                          type="button"
                          onClick={(e) => {
                            toggleDropdown(customer.id);
                            e.stopPropagation();
                          }}
                        >
                          <FontAwesomeIcon icon={faEllipsisVertical} />
                        </button>
                        {activeDropdown === customer.id && (
                          <div
                            className="absolute right-0 mt-2 w-48 bg-white border rounded-lg shadow-lg z-10"
                            ref={(el) => {
                              if (el) {
                                dropdownRefs.current.set(customer.id, el);
                              } else {
                                dropdownRefs.current.delete(customer.id);
                              }
                            }}
                          >
                            <ul className="py-2 text-left">
                              <li className="mx-2 my-0">
                                <button
                                  type="button"
                                  className="w-full px-4 py-2 text-sm font-semibold hover:bg-gray-100 cursor-pointer rounded-md text-left"
                                  onClick={() =>
                                    navigate('/customer/details/' + customer.id)
                                  }
                                >
                                  Update Customer Info
                                </button>
                              </li>
                              <li className="mx-2 my-0">
                                <button
                                  type="button"
                                  disabled={isSubscriptionActive}
                                  className={`w-full px-4 py-2 text-sm font-semibold ${isSubscriptionActive ? 'cursor-not-allowed text-gray-300' : 'cursor-pointer'} hover:bg-gray-100 cursor-pointer rounded-md text-left`}
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    handleCreateNewOrder(customer.id);
                                  }}
                                >
                                  Create New Order
                                </button>
                              </li>

                              {/* <li className="mx-2 my-0">
                                <button
                                  type="button"
                                  className="w-full px-4 py-2 text-sm font-semibold hover:bg-gray-100 cursor-pointer rounded-md text-left"
                                  onClick={() => {
                                    //Returns a path with params interpolated.
                                    navigate(
                                      generatePath(
                                        FrontendRoutes.CUSTOMER_DETAILS,
                                        { customerId: customer.id },
                                      ),
                                    );
                                  }}
                                >
                                  Delete Customer
                                </button>
                              </li> */}
                            </ul>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={4} className=" px-4 py-2 text-center">
                      No data available.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </form>

          {/* Pagination */}
          <div className="bottom-0">
            <Pagination
              totalPages={totalPages}
              currentPage={currentPage}
              itemsPerPage={itemsPerPage}
              onPageChange={setCurrentPage}
              onItemsPerPageChange={setItemsPerPage}
            />
          </div>
        </div>
        {/* Button
        <div className="mt-auto pt-4">
          <button
            type="button"
            onClick={() => navigate(FrontendRoutes.CREATE_CUSTOMER)}
            className="w-4/5 px-6 py-2 mb-4 border border-transparent rounded-sm text-sm font-medium text-white bg-primary hover:bg-dark-primary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary flex items-center justify-center space-x-2"
          >
            + Add New Customer
          </button>
        </div> */}
      </div>
    </div>
  );
};
